/**
 * Serviço para gerenciar chamadas de API
 */

import { getToken } from "./tokenService";
import type {
  ApiCall,
  ApiCallDetail,
  ApiCallsListResponse,
  ApiCallsStats,
  ApiCallsFilterOptions,
} from "../types/apiCalls";

import { API_BASE_URL } from "@/lib/apiConfig";

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: string;
  };
  message?: string;
}

/**
 * Faz uma requisição autenticada para a API
 */
async function apiRequest<T>(
  endpoint: string,
  method: string = "GET",
  body?: any
): Promise<ApiResponse<T>> {
  const token = getToken();

  if (!token) {
    throw new Error("Token de autenticação não encontrado");
  }

  const config: RequestInit = {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  if (body && method !== "GET") {
    config.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || {
          code: "HTTP_ERROR",
          message: `Erro HTTP: ${response.status}`,
        },
      };
    }

    return data;
  } catch (error) {
    return {
      success: false,
      error: {
        code: "NETWORK_ERROR",
        message:
          error instanceof Error ? error.message : "Erro de conexão com a API",
      },
    };
  }
}

/**
 * Lista chamadas de API com filtros e paginação
 */
export async function listApiCalls(
  options?: ApiCallsFilterOptions
): Promise<ApiResponse<ApiCallsListResponse>> {
  const params = new URLSearchParams();

  if (options?.limit != null) params.append("limit", options.limit.toString());
  if (options?.offset != null) params.append("offset", options.offset.toString());
  if (options?.client_id !== undefined && options?.client_id !== null) {
    if (options.client_id === "null" || options.client_id !== "all") {
      params.append("client_id", options.client_id);
    }
  }
  if (options?.product_id) params.append("product_id", options.product_id);
  if (options?.type_id) params.append("type_id", options.type_id.toString());
  if (options?.status_id)
    params.append("status_id", options.status_id.toString());
  if (options?.start_date) params.append("start_date", options.start_date);
  if (options?.end_date) params.append("end_date", options.end_date);
  if (options?.search) params.append("search", options.search);

  const queryString = params.toString();
  return apiRequest<ApiCallsListResponse>(
    `/api-calls${queryString ? `?${queryString}` : ""}`
  );
}

export async function listAllApiCallsMatching(
  options: Omit<ApiCallsFilterOptions, "limit" | "offset">,
  pageSize: number = 200
): Promise<ApiResponse<ApiCall[]>> {
  const all: ApiCall[] = [];
  let offset = 0;
  let total = Number.POSITIVE_INFINITY;

  while (offset < total) {
    const response = await listApiCalls({
      ...options,
      limit: pageSize,
      offset,
    });

    if (!response.success || !response.data) {
      return {
        success: false,
        error: response.error || {
          code: "EXPORT_ERROR",
          message: "Erro ao buscar chamadas para exportação",
        },
      };
    }

    all.push(...response.data.api_calls);
    total = response.data.pagination?.total ?? response.data.total ?? all.length;
    offset += pageSize;

    if (response.data.api_calls.length === 0) {
      break;
    }
  }

  return { success: true, data: all };
}

/**
 * Obtém uma chamada de API por ID com todos os detalhes
 */
export async function getApiCallById(
  id: string
): Promise<ApiResponse<ApiCallDetail>> {
  return apiRequest<ApiCallDetail>(`/api-calls/${id}`);
}

/**
 * Obtém estatísticas das chamadas de API
 */
export async function getApiCallsStats(): Promise<
  ApiResponse<ApiCallsStats>
> {
  return apiRequest<ApiCallsStats>("/api-calls/stats");
}

/**
 * Interface para KPIs do dashboard
 */
export interface DashboardKpis {
  chamadas_totais: {
    value: number;
    variation: number;
    previous_value: number;
  };
  tokens_consumidos: {
    value: number;
    variation: number;
    previous_value: number;
  };
  custo_total: {
    value: number;
    variation: number;
    previous_value: number;
  };
  taxa_erro: {
    value: number;
    variation: number;
    previous_value: number;
  };
  period: {
    month: number;
    year: number;
    start_date: string;
    end_date: string;
  };
  previous_period: {
    month: number;
    year: number;
    start_date: string;
    end_date: string;
  };
}

export interface DashboardKpisOptions {
  month: number;
  year: number;
  client_id?: string | null;
}

/**
 * Obtém KPIs do dashboard com filtros
 */
export async function getDashboardKpis(
  options: DashboardKpisOptions
): Promise<ApiResponse<DashboardKpis>> {
  const params = new URLSearchParams();
  params.append("month", options.month.toString());
  params.append("year", options.year.toString());

  if (options.client_id !== undefined && options.client_id !== null) {
    if (options.client_id === "null") {
      params.append("client_id", "null");
    } else if (options.client_id !== "all") {
      params.append("client_id", options.client_id);
    }
  }

  return apiRequest<DashboardKpis>(
    `/api-calls/dashboard/kpis?${params.toString()}`
  );
}

/**
 * Interface para períodos disponíveis
 */
export interface AvailablePeriods {
  years: number[];
  months_by_year: Record<number, number[]>;
}

export interface AvailablePeriodsOptions {
  client_id?: string | null;
}

/**
 * Obtém períodos disponíveis (anos e meses) com dados
 */
export async function getAvailablePeriods(
  options?: AvailablePeriodsOptions
): Promise<ApiResponse<AvailablePeriods>> {
  const params = new URLSearchParams();

  if (options?.client_id !== undefined && options.client_id !== null) {
    if (options.client_id === "null") {
      params.append("client_id", "null");
    } else if (options.client_id !== "all") {
      params.append("client_id", options.client_id);
    }
  }

  const queryString = params.toString();
  return apiRequest<AvailablePeriods>(
    `/api-calls/dashboard/available-periods${queryString ? `?${queryString}` : ""}`
  );
}

/**
 * Interface para dados de chamadas por período
 */
export interface CallsByPeriodData {
  name: string;
  success: number;
  error: number;
}

export interface CallsByPeriodOptions {
  period?: "1h" | "24h" | "7d" | "30d";
  client_id?: string | null;
  current_date?: string; // Data atual do navegador (formato: YYYY-MM-DD)
}

/**
 * Obtém dados de chamadas agrupadas por período
 */
export async function getCallsByPeriod(
  options?: CallsByPeriodOptions
): Promise<ApiResponse<CallsByPeriodData[]>> {
  const params = new URLSearchParams();

  if (options?.period) {
    params.append("period", options.period);
  }

  if (options?.client_id !== undefined && options.client_id !== null) {
    if (options.client_id === "null") {
      params.append("client_id", "null");
    } else if (options.client_id !== "all") {
      params.append("client_id", options.client_id);
    }
  }

  if (options?.current_date) {
    params.append("current_date", options.current_date);
  }

  const queryString = params.toString();
  return apiRequest<CallsByPeriodData[]>(
    `/api-calls/dashboard/calls-by-period${queryString ? `?${queryString}` : ""}`
  );
}

/**
 * Interface para dados de custos por período
 */
export interface CostsByPeriodData {
  name: string;
  custoAtual: number;
  custoAnterior: number;
}

export interface CostsByPeriodOptions {
  period?: "1h" | "24h" | "7d" | "30d";
  client_id?: string | null;
  current_date?: string; // Data atual do navegador (formato: YYYY-MM-DD)
}

/**
 * Obtém dados de custos agrupados por período
 */
export async function getCostsByPeriod(
  options?: CostsByPeriodOptions
): Promise<ApiResponse<CostsByPeriodData[]>> {
  const params = new URLSearchParams();

  if (options?.period) {
    params.append("period", options.period);
  }

  if (options?.client_id !== undefined && options.client_id !== null) {
    if (options.client_id === "null") {
      params.append("client_id", "null");
    } else if (options.client_id !== "all") {
      params.append("client_id", options.client_id);
    }
  }

  if (options?.current_date) {
    params.append("current_date", options.current_date);
  }

  const queryString = params.toString();
  return apiRequest<CostsByPeriodData[]>(
    `/api-calls/dashboard/costs-by-period${queryString ? `?${queryString}` : ""}`
  );
}

/**
 * Interface para dados de tokens por tipo
 */
export interface TokensByTypeData {
  name: string;
  input: number;
  output: number;
}

export interface TokensByTypeOptions {
  period?: "1h" | "24h" | "7d" | "30d";
  client_id?: string | null;
  current_date?: string; // Data atual do navegador (formato: YYYY-MM-DD)
}

/**
 * Obtém dados de tokens agrupados por tipo
 */
export async function getTokensByType(
  options?: TokensByTypeOptions
): Promise<ApiResponse<TokensByTypeData[]>> {
  const params = new URLSearchParams();

  if (options?.period) {
    params.append("period", options.period);
  }

  if (options?.client_id !== undefined && options.client_id !== null) {
    if (options.client_id === "null") {
      params.append("client_id", "null");
    } else if (options.client_id !== "all") {
      params.append("client_id", options.client_id);
    }
  }

  // Se current_date foi fornecido, adicionar; caso contrário, para período 1h, 24h ou 30d, enviar data atual do navegador
  if (options?.current_date) {
    params.append("current_date", options.current_date);
  } else if (options?.period === "1h" || options?.period === "24h" || options?.period === "30d") {
    // Enviar data atual do navegador no formato YYYY-MM-DD
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    params.append("current_date", `${year}-${month}-${day}`);
  }

  const queryString = params.toString();
  return apiRequest<TokensByTypeData[]>(
    `/api-calls/dashboard/tokens-by-type${queryString ? `?${queryString}` : ""}`
  );
}

/**
 * Lista os tipos de chamadas de API
 */
export interface ApiCallTypeOption {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
}

export async function listApiCallTypes(): Promise<
  ApiResponse<ApiCallTypeOption[]>
> {
  return apiRequest<ApiCallTypeOption[]>("/types/api-call-types");
}

/**
 * Lista os status de chamadas de API
 */
export interface ApiCallStatusOption {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
}

export async function listApiCallStatuses(): Promise<
  ApiResponse<ApiCallStatusOption[]>
> {
  return apiRequest<ApiCallStatusOption[]>("/types/api-call-status");
}

export interface RealConsumptionByModel {
  model_id: string | null;
  model_name: string;
  model_identifier: string | null;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cost: number;
  calls: number;
}

export interface RealConsumptionData {
  period: "1h" | "24h" | "7d" | "30d";
  from: string;
  to: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  /** Custo do provedor (SUM tb_api_call.cost) — não é preço de venda */
  cost: number;
  calls: number;
  by_model: RealConsumptionByModel[];
}

export interface RealConsumptionOptions {
  period?: "1h" | "24h" | "7d" | "30d";
  client_id?: string | null;
}

/**
 * Consumo real de tokens e custo do provedor por período (sem estimativas).
 */
export async function getRealConsumptionByPeriod(
  options?: RealConsumptionOptions,
): Promise<ApiResponse<RealConsumptionData>> {
  const params = new URLSearchParams();

  if (options?.period) {
    params.append("period", options.period);
  }

  if (options?.client_id === "null") {
    params.append("client_id", "null");
  } else if (options?.client_id && options.client_id !== "all") {
    params.append("client_id", options.client_id);
  }

  const queryString = params.toString();
  return apiRequest<RealConsumptionData>(
    `/api-calls/dashboard/real-consumption${queryString ? `?${queryString}` : ""}`,
  );
}

export interface EtegModelConsumption {
  model_id: string | null;
  model_name: string;
  model_identifier: string | null;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cost: number;
  calls: number;
  error_calls: number;
  error_rate: number;
}

export interface EtegProductConsumption {
  product_id: string | null;
  product_name: string;
  calls: number;
  total_tokens: number;
  cost: number;
}

export interface EtegSuccessError {
  success: number;
  error: number;
  total: number;
  success_rate: number;
  error_rate: number;
}

export interface EtegDashboardOverview {
  from: string;
  to: string;
  by_model: EtegModelConsumption[];
  by_product: EtegProductConsumption[];
  success_error: EtegSuccessError;
}

export interface EtegDashboardOverviewOptions {
  period?: string;
  client_id?: string | null;
  product_id?: string | null;
  start_date?: string;
  end_date?: string;
}

export async function getEtegDashboardOverview(
  options?: EtegDashboardOverviewOptions,
): Promise<ApiResponse<EtegDashboardOverview>> {
  const params = new URLSearchParams();

  if (options?.period) params.append("period", options.period);
  if (options?.start_date) params.append("start_date", options.start_date);
  if (options?.end_date) params.append("end_date", options.end_date);
  if (options?.product_id && options.product_id !== "all") {
    params.append("product_id", options.product_id);
  }
  if (options?.client_id === "null") {
    params.append("client_id", "null");
  } else if (options?.client_id && options.client_id !== "all") {
    params.append("client_id", options.client_id);
  }

  const queryString = params.toString();
  return apiRequest<EtegDashboardOverview>(
    `/api-calls/dashboard/eteg-overview${queryString ? `?${queryString}` : ""}`,
  );
}

